import { ElementType, ReactNode, forwardRef, KeyboardEvent, MouseEvent } from 'react';
import { CSSObject, SerializedStyles } from '@emotion/react';
import { theme } from '@frontend/theme';
import { KeyNames } from '../../../../constants';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../../../type-utils';
import { ContextMenu, ContextMenuActions } from '../../../context-menu/context-menu';
import { SpinningLoader } from '../../../loader';
import { useChatBubbleContext } from '../../chat';
import { ChatStyles } from '../../chat.styles';
import { AiActivityBubble } from './ai-activity-bubble';
import { CallBubble } from './call-bubble';
import { ImageBubble } from './image-bubble';
import { TextBubble } from './text-bubble';

export type BubbleProps = {
  text?: string | ReactNode;
  subText?: ReactNode;
  bubbleIcon?: ReactNode;
  onClick?: (e?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
  contextActions?: ContextMenuActions;
  contextMenuReturnFocus?: boolean;
  isDeleted?: boolean;
  styles?: SerializedStyles;
  variant: 'text' | 'deleted' | 'image' | 'call' | 'ai-activity';
  src: string;
  maxWidth: string;
  maxHeight: string;
  imageProps?: {
    suppressPreview?: boolean;
    onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
    onClickDownload?: () => void;
    downloadImageTrackingId?: string;
    backgroundColor?: (typeof theme.colors)[keyof typeof theme.colors];
    css?: CSSObject;
  };
  callProps?: {
    callResult?: 'missed' | 'inbound' | 'outbound' | 'abandoned' | 'voicemail';
    callAction: () => void;
    playAction?: () => void;
  };
  leadingIcon?: ReactNode;
};

type PolymorphicBubbleProps<C extends ElementType = 'li'> = PolymorphicComponentPropWithRef<C, BubbleProps>;

const BubbleComponent = <C extends ElementType = 'li'>(
  {
    as,
    text,
    subText,
    bubbleIcon,
    onClick,
    contextActions,
    contextMenuReturnFocus,
    variant = 'text',
    src,
    imageProps,
    backgroundColor,
    styles,
    maxWidth,
    callProps,
    maxHeight,
    leadingIcon,
  }: PolymorphicBubbleProps<C>,
  ref: PolymorphicRef<C>
) => {
  const Component = as || 'li';
  const { direction, isSending } = useChatBubbleContext();
  const bubbleStyles = ChatStyles.bubble({
    direction,
    isImage: variant === 'image',
    backgroundColor,
    maxWidth,
    maxHeight,
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === KeyNames.Enter || event.key === KeyNames.Space) {
      onClick?.(event);
    }
  };

  if (isSending || (variant === 'image' && !src)) {
    return (
      <div className={variant === 'image' ? 'image-loader' : ''}>
        <SpinningLoader size='medium' />
      </div>
    );
  }

  if (!contextActions?.length) {
    return (
      <Component ref={ref} onClick={onClick} onKeyDown={handleKeyDown} tabIndex={0} css={[bubbleStyles, styles]}>
        {bubbleIcon}
        {variant === 'image' ? (
          <ImageBubble src={src} {...imageProps} />
        ) : variant === 'call' ? (
          <CallBubble
            title={text}
            subtitle={subText}
            callResult={callProps?.callResult}
            callAction={callProps?.callAction}
            playAction={callProps?.playAction}
          />
        ) : variant === 'ai-activity' ? (
          <AiActivityBubble text={text} leadingIcon={leadingIcon} />
        ) : (
          <TextBubble text={text} subText={subText} variant={variant} />
        )}
      </Component>
    );
  }

  return (
    <ContextMenu actions={contextActions ?? []} returnFocus={contextMenuReturnFocus}>
      <Component ref={ref} onClick={onClick} onKeyDown={handleKeyDown} tabIndex={0} css={[bubbleStyles, styles]}>
        {bubbleIcon}
        {variant === 'image' ? (
          <ImageBubble src={src} {...imageProps} />
        ) : variant === 'call' ? (
          <CallBubble
            title={text}
            subtitle={subText}
            callResult={callProps?.callResult}
            callAction={callProps?.callAction}
            playAction={callProps?.playAction}
          />
        ) : variant === 'ai-activity' ? (
          <AiActivityBubble text={text} leadingIcon={leadingIcon} />
        ) : (
          <TextBubble text={text} subText={subText} variant={variant} />
        )}
      </Component>
    </ContextMenu>
  );
};

export const Bubble = forwardRef(BubbleComponent);
Bubble.displayName = 'Bubble';
